import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { DOC_TYPES, DOC_TYPE_BY_PATH, DOC_STATUS_OPTIONS } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import QuickPartyModal from "../components/QuickPartyModal";
import QuickProductModal from "../components/QuickProductModal";
import { computeDocTotals, resolveDestState } from "../lib/docTotals";
import { useAuth } from "../context/AuthContext";
import { DocHeaderCard } from "../components/docform/DocHeaderCard";
import { LineItemsTable } from "../components/docform/LineItemsTable";
import { DocExtraSections } from "../components/docform/DocExtraSections";
import { DocSummaryCard } from "../components/docform/DocSummaryCard";

let _lineSeq = 0;
const emptyLine = () => ({
  _uid: `ln-${++_lineSeq}-${Date.now()}`,
  product_id: "", name: "", description: "", hsn: "", unit: "NOS",
  quantity: 1, rate: 0, discount_pct: 0, gst_rate: 18,
});

const emptyInline = () => ({
  name: "", gstin: "URP", phone: "", email: "",
  billing_address: "", city: "", state: "", state_code: "", pincode: "",
});

export default function DocumentFormPage({ mode }) {
  const { docPath, id } = useParams();
  const docType = DOC_TYPE_BY_PATH[docPath];
  const cfg = DOC_TYPES[docType];
  const nav = useNavigate();

  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [company, setCompany] = useState({ state_code: "09" });
  const [saving, setSaving] = useState(false);
  const [series, setSeries] = useState(null);
  const [quickPartyOpen, setQuickPartyOpen] = useState(false);
  // Quick-add product: track which line row (index) triggered the modal so we can auto-select the new product there.
  const [quickProductIdx, setQuickProductIdx] = useState(null);
  // partyMode: "existing" | "urp"
  const [partyMode, setPartyMode] = useState("existing");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [missingNumbers, setMissingNumbers] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const showPosting = ["invoice", "repair_invoice", "credit_note"].includes(docType);
  useEffect(() => {
    if (showPosting) api.get("/accounting/ledgers").then(r => setLedgers(r.data)).catch((err) => console.error("Ledgers load failed:", err));
  }, [showPosting]);

  const [form, setForm] = useState({
    doc_type: docType,
    doc_date: new Date().toISOString().slice(0, 10),
    party_id: "",
    party_inline: emptyInline(),
    place_of_supply_state_code: "",
    reference: "",
    notes: "",
    dr_ledger_id: "",
    cr_ledger_id: "",
    terms: "",
    items: [emptyLine()],
    transporter_name: "",
    transporter_id: "",
    vehicle_no: "",
    transport_mode: "Road",
    distance_km: 0,
    eway_bill_no: "",
    status: (DOC_STATUS_OPTIONS[docType] || ["draft"])[0],
    driver_name: "",
    gate_pass_purpose: "Outward",
    expected_delivery_date: "",
    advance_amount: 0,
    advance_mode: "",
    advance_reference: "",
    serial_number: "",
    item_make_model: "",
    issue_description: "",
    purpose_of_job_work: "",
    expected_return_date: "",
    item_condition: "Good",
    is_returnable: true,
    extra_discount_type: "amount",
    extra_discount_value: 0,
    rate_includes_gst: false,
    doc_number_override: "",
  });

  useEffect(() => {
    const kind = cfg.party;
    Promise.all([
      api.get("/parties", { params: { kind } }),
      api.get("/products"),
      api.get("/company"),
      api.get("/settings/document-series"),
    ]).then(([p, pr, c, s]) => {
      setParties(p.data);
      setProducts(pr.data);
      setCompany(c.data || { state_code: "09" });
      setSeries(s.data);
    });
  }, [cfg.party]);

  useEffect(() => {
    if (mode === "new" && isAdmin) {
      api.get("/documents/missing-numbers", { params: { doc_type: docType } })
        .then(r => setMissingNumbers(r.data.missing || []))
        .catch((err) => { console.error('Missing-numbers lookup failed:', err); setMissingNumbers([]); });
    }
  }, [mode, isAdmin, docType]);

  useEffect(() => {
    if (mode === "edit" && id) {
      api.get(`/documents/${id}`).then(r => {
        const d = r.data;
        const isUrp = !d.party_id && d.party_snapshot?.is_urp;
        setPartyMode(isUrp ? "urp" : "existing");
        setForm(f => ({
          ...f,
          ...d,
          party_id: d.party_id || "",
          party_inline: isUrp ? {
            name: d.party_snapshot?.name || "",
            gstin: d.party_snapshot?.gstin || "URP",
            phone: d.party_snapshot?.phone || "",
            email: d.party_snapshot?.email || "",
            billing_address: d.party_snapshot?.billing_address || "",
            city: d.party_snapshot?.city || "",
            state: d.party_snapshot?.state || "",
            state_code: d.party_snapshot?.state_code || "",
            pincode: d.party_snapshot?.pincode || "",
          } : emptyInline(),
          items: (d.items && d.items.length ? d.items : [emptyLine()]).map((it, idx) => ({
            _uid: it._uid || `ln-load-${idx}-${Date.now()}`,
            product_id: it.product_id || "",
            name: it.name || "",
            description: it.description || "",
            hsn: it.hsn || "",
            unit: it.unit || "NOS",
            quantity: it.quantity || 0,
            // If doc was saved with inclusive rate flag, show the inclusive rate the user originally typed
            rate: (d.rate_includes_gst && it.rate_inclusive ? it.rate_inclusive : it.rate) || 0,
            discount_pct: it.discount_pct || 0,
            gst_rate: it.gst_rate || 0,
          })),
          place_of_supply_state_code: d.place_of_supply_state_code || "",
          rate_includes_gst: !!d.rate_includes_gst,
          extra_discount_type: d.extra_discount_type || "amount",
          extra_discount_value: d.extra_discount_value || 0,
          doc_number_override: "",  // don't pre-fill override; backend keeps existing number
        }));
      });
    }
  }, [mode, id]);

  const companyState = company?.state_code || "09";
  const selectedParty = useMemo(() => parties.find(p => p.id === form.party_id), [parties, form.party_id]);
  const destState = resolveDestState({ form, party: selectedParty, partyMode, companyState });
  const totals = useMemo(() => computeDocTotals({ form, destState, companyState, showAdvance: cfg.showAdvance }),
    [form, destState, companyState, cfg.showAdvance]);

  const setItem = (i, patch) => {
    setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  };

  const pickProduct = (i, productId) => {
    const p = products.find(x => x.id === productId);
    if (!p) { setItem(i, { product_id: "" }); return; }
    setItem(i, {
      product_id: p.id,
      name: p.name,
      description: p.description || "",
      hsn: p.hsn || "",
      unit: p.unit || "NOS",
      rate: Number(p.rate || 0),
      gst_rate: Number(p.gst_rate || 0),
    });
  };

  const addRow = () => setForm(f => ({ ...f, items: [...f.items, emptyLine()] }));
  const removeRow = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const lookupPincode = async (pin) => {
    const v = String(pin || "").replace(/[^0-9]/g, "");
    if (v.length !== 6) return;
    try {
      const { data } = await api.get(`/lookup/pincode/${v}`);
      setForm(f => ({
        ...f,
        party_inline: {
          ...f.party_inline,
          pincode: v,
          city: data.city || f.party_inline.city,
          state: data.state || f.party_inline.state,
          state_code: data.state_code || f.party_inline.state_code,
        },
      }));
      toast.success(`Auto-filled: ${data.city}, ${data.state}`);
    } catch {
      // silent — user can still fill manually
    }
  };

  const submit = async () => {
    if (!form.doc_date) return toast.error("Date is required");
    if (form.items.length === 0 || form.items.every(i => !i.name)) return toast.error("At least one line item is required");
    if (partyMode === "existing" && !form.party_id) return toast.error(`Select a ${cfg.party} or switch to URP / Cash customer`);
    if (partyMode === "urp" && !form.party_inline?.name) return toast.error("Enter the customer name (URP)");
    setSaving(true);
    try {
      const payload = {
        ...form,
        party_id: partyMode === "existing" ? form.party_id : null,
        party_inline: partyMode === "urp" ? form.party_inline : null,
        items: form.items.filter(i => i.name).map(i => ({
          ...i,
          quantity: Number(i.quantity || 0),
          rate: Number(i.rate || 0),
          discount_pct: Number(i.discount_pct || 0),
          gst_rate: Number(i.gst_rate || 0),
        })),
        distance_km: Number(form.distance_km || 0),
        rate_includes_gst: !!form.rate_includes_gst,
        extra_discount_type: form.extra_discount_type || "amount",
        extra_discount_value: Number(form.extra_discount_value || 0),
        advance_amount: Number(form.advance_amount || 0),
        dr_ledger_id: form.dr_ledger_id || "",
        cr_ledger_id: form.cr_ledger_id || "",
        doc_number_override: (form.doc_number_override || "").trim() || null,
      };
      const resp = mode === "edit"
        ? await api.put(`/documents/${id}`, payload)
        : await api.post("/documents", payload);
      toast.success(`${cfg.label} saved`);
      nav(`/${cfg.path}/${resp.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`doc-form-${docType}`}>
      <PageHeader
        title={mode === "edit" ? `Edit ${cfg.label}` : `New ${cfg.label}`}
        subtitle={cfg.party === "vendor" ? "Vendor document" : "Customer document"}
        actions={
          <>
            <Link to={`/${cfg.path}`} className="btn-secondary inline-flex items-center gap-1" data-testid="cancel-doc"><ArrowLeft size={14} /> Back</Link>
            <button onClick={submit} disabled={saving} className="btn-primary" data-testid="save-doc">{saving ? "Saving…" : "Save"}</button>
          </>
        }
      />

      <div className="p-8 space-y-6">
        <DocHeaderCard form={form} setForm={setForm} cfg={cfg} docType={docType} mode={mode} parties={parties}
          partyMode={partyMode} setPartyMode={setPartyMode} setQuickPartyOpen={setQuickPartyOpen} lookupPincode={lookupPincode}
          series={series} isAdmin={isAdmin} missingNumbers={missingNumbers} totals={totals} company={company} />

        <LineItemsTable form={form} setForm={setForm} cfg={cfg} products={products} setItem={setItem} pickProduct={pickProduct}
          addRow={addRow} removeRow={removeRow} setQuickProductIdx={setQuickProductIdx} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <DocExtraSections form={form} setForm={setForm} cfg={cfg} showPosting={showPosting} ledgers={ledgers} />
          <DocSummaryCard form={form} setForm={setForm} cfg={cfg} totals={totals} />
        </div>
      </div>
      {quickPartyOpen && (
        <QuickPartyModal
          kind={cfg.party}
          onClose={() => setQuickPartyOpen(false)}
          onCreated={(p) => {
            setParties(prev => [p, ...prev]);
            setForm(f => ({ ...f, party_id: p.id }));
            setQuickPartyOpen(false);
          }}
        />
      )}
      {quickProductIdx !== null && (
        <QuickProductModal
          initialName={form.items[quickProductIdx]?.name || ""}
          onClose={() => setQuickProductIdx(null)}
          onCreated={(p) => {
            setProducts(prev => [p, ...prev]);
            // Auto-fill the line that triggered the modal with the new product
            setForm(f => ({
              ...f,
              items: f.items.map((it, idx) => idx === quickProductIdx ? {
                ...it,
                product_id: p.id,
                name: p.name,
                description: p.description || "",
                hsn: p.hsn || "",
                unit: p.unit || "NOS",
                rate: Number(p.rate || 0),
                gst_rate: Number(p.gst_rate || 0),
              } : it),
            }));
            setQuickProductIdx(null);
          }}
        />
      )}
      <FieldStyle />
    </div>
  );
}
